import { useState } from 'react';
import { apiAdminService } from '@api';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { useParams } from 'react-router-dom';
import { TCreateDocument, TDocumentFilter, TUpdateDocument } from '@xeppt/xeppt-sdk/types';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { useLocales } from '@hooks/helpers/useLocales';
import { toast } from 'react-toastify';

interface IProps {
    isInitialAgreements?: boolean;
    isInitialAgreement?: boolean;
    agreementId?: string;
}

export const useAgreements = ({
    isInitialAgreements = false,
    isInitialAgreement = false,
    agreementId
}: IProps) => {
    const [filter, setFilter] = useState<TDocumentFilter>({});
    const { requestErrorLocale } = useLocales();

    const { data: agreements, handleRequest: refetchAgreements } = useApiQuery({
        method: () => apiAdminService.getDocuments(10, 1, { ...filter, name: 'card_agreement' }),
        isInitialRequest: isInitialAgreements,
        deps: [filter]
    });

    const { data: agreement } = useApiQuery({
        method: () => apiAdminService.getDocumentById(agreementId || ''),
        condition: !!agreementId,
        deps: [agreementId],
        isInitialRequest: isInitialAgreement
    });

    const { handleRequest: handleCreateDocument, isLoading: isCreateDocumentLoading } =
        useApiMutation({
            method: (payload: Omit<TCreateDocument, 'name'>) =>
                apiAdminService.createDocument({ ...payload, name: 'card_agreement' }),
            onError: requestErrorLocale,
            onSuccess: () => {
                toast.success('Agreement was successfully created');
            }
        });

    const { handleRequest: handleSetAsPrimary, isLoading: isSetAsPrimaryDocumentLoading } =
        useApiMutation({
            method: (value: string) => apiAdminService.updateDocument(value, { is_primary: true }),
            onError: requestErrorLocale,
            onSuccess: () => {
                toast.success('Agreement was successfully set as primary');
            }
        });

    const handleChangeAgreementFilter = (data: Partial<typeof filter>) => {
        setFilter((state) => ({ ...state, ...data }));
    };

    return {
        agreements,
        refetchAgreements,
        agreement,
        handleChangeAgreementFilter,
        handleCreateDocument,
        handleSetAsPrimary,
        filter,
        isCreateDocumentLoading,
        isSetAsPrimaryDocumentLoading
    };
};
